import React, { useState } from 'react';
import { Youtube, Music, Apple, ArrowUpRight, Mail, X, Send, Loader2 } from 'lucide-react';
import { siteConfig, podcastPlatforms, navLinks } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// X (Twitter) icon component
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { isDark } = useTheme();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setIsContactOpen(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const platformIcons = {
    Youtube: Youtube,
    Music: Music,
    Apple: Apple
  };

  const socialLinks = [
    { name: 'X (Twitter)', url: 'https://x.com/JarrettVaughan', icon: XIcon }
  ];

  return (
    <>
      <footer className={`border-t ${isDark ? 'bg-stone-950 border-stone-900' : 'bg-stone-50 border-stone-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="py-16 grid md:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="md:col-span-2 space-y-6">
              <div className="flex items-center" data-testid="footer-logo">
                <img 
                  src="/logo.svg" 
                  alt={siteConfig.name}
                  className="h-10 sm:h-12 w-auto"
                />
              </div>
              
              <p className={`max-w-md leading-relaxed ${isDark ? 'text-stone-500' : 'text-stone-600'}`}>
                {siteConfig.heroSubtext}
              </p>

              {/* Platform Links */}
              <div className="flex gap-3">
                {podcastPlatforms.map((platform) => {
                  const Icon = platformIcons[platform.icon];
                  return (
                    <a
                      key={platform.name}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 border rounded-lg flex items-center justify-center transition-all ${
                        isDark 
                          ? 'bg-stone-900 hover:bg-amber-500/10 border-stone-800 hover:border-amber-500/30 text-stone-500 hover:text-amber-400' 
                          : 'bg-white hover:bg-amber-50 border-stone-200 hover:border-amber-500/30 text-stone-500 hover:text-amber-600'
                      }`}
                      aria-label={platform.name}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>

              {/* Contact Us Button */}
              <Button
                onClick={() => setIsContactOpen(true)}
                variant="outline"
                className={`flex items-center gap-2 ${
                  isDark 
                    ? 'border-stone-700 text-stone-300 hover:bg-stone-800 hover:text-amber-400 hover:border-amber-500/30' 
                    : 'border-stone-300 text-stone-600 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-500/30'
                }`}
                data-testid="contact-us-btn"
              >
                <Mail className="w-4 h-4" />
                Contact Us
              </Button>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className={`font-semibold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>Quick Links</h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.name}>
                    {link.isExternal ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`transition-colors ${isDark ? 'text-stone-500 hover:text-amber-400' : 'text-stone-600 hover:text-amber-600'}`}
                      >
                        {link.name}
                      </a>
                    ) : (
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className={`transition-colors text-left ${isDark ? 'text-stone-500 hover:text-amber-400' : 'text-stone-600 hover:text-amber-600'}`}
                      >
                        {link.name}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Listen On & Follow Us */}
            <div className="space-y-8">
              {/* Listen On */}
              <div>
                <h4 className={`font-semibold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>Listen On</h4>
                <ul className="space-y-3">
                  {podcastPlatforms.map((platform) => (
                    <li key={platform.name}>
                      <a
                        href={platform.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 group transition-colors ${isDark ? 'text-stone-500 hover:text-amber-400' : 'text-stone-600 hover:text-amber-600'}`}
                      >
                        {platform.name}
                        <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Follow Us On */}
              <div>
                <h4 className={`font-semibold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>Follow Us On</h4>
                <ul className="space-y-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <li key={social.name}>
                        <a
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 group transition-colors ${isDark ? 'text-stone-500 hover:text-amber-400' : 'text-stone-600 hover:text-amber-600'}`}
                        >
                          <Icon className="w-4 h-4" />
                          {social.name}
                          <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={`py-6 border-t flex flex-col sm:flex-row justify-between items-center gap-4 ${isDark ? 'border-stone-900' : 'border-stone-200'}`}>
            <p className={`text-sm ${isDark ? 'text-stone-600' : 'text-stone-500'}`}>
              © {currentYear} {siteConfig.name}. All rights reserved.
            </p>
            <p className={`text-sm ${isDark ? 'text-stone-600' : 'text-stone-500'}`}>
              Live Free. Stay Sovereign.
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsContactOpen(false)}
          />
          
          {/* Modal */}
          <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl ${
            isDark ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-200'
          }`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-stone-800' : 'border-stone-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                  <Mail className={`w-5 h-5 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                </div>
                <div>
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                    Contact Us
                  </h2>
                  <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                    We'd love to hear from you
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsContactOpen(false)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-stone-800 text-stone-500' : 'hover:bg-stone-100 text-stone-400'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                  Name
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  className={isDark ? 'bg-stone-800 border-stone-700 text-stone-100' : 'bg-stone-50 border-stone-200'}
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                  Email
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  className={isDark ? 'bg-stone-800 border-stone-700 text-stone-100' : 'bg-stone-50 border-stone-200'}
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                  Subject
                </label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="What's this about?"
                  className={isDark ? 'bg-stone-800 border-stone-700 text-stone-100' : 'bg-stone-50 border-stone-200'}
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                  Message
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Your message..."
                  rows={4}
                  className={`resize-none ${isDark ? 'bg-stone-800 border-stone-700 text-stone-100' : 'bg-stone-50 border-stone-200'}`}
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold h-11"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
